/**
 * Files dropped anywhere in the window: where they go is the page's say. A page (or the space around it) names a
 * target while it's shown — the item's attachments on its page, the triage area elsewhere in a space — and the window's
 * overlay (ui/FileDrop.tsx) says what dropping does and hands the files to it. Drop zones in a page take the same
 * targets, so an upload shows its progress on them however it was started.
 */

import { signal } from "@preact/signals";
import { useEffect, useRef } from "preact/hooks";
import { asApiError } from "./resource.ts";
import { toast } from "./toast.ts";

/** Where dropped (or picked) files go, and what's said of it. */
export interface DropTarget {
  /** What it uploads into: the same key on a page's drop zone shows the upload under way, wherever it began. */
  key: string;
  /** What dropping does, on the overlay ("Drop to attach to Invoice 1042"); without `take`, why nothing would. */
  label: string;
  /** While it uploads ("Attaching…"). */
  busy: string;
  /** The space's own (0) or a page's (1): the highest shown takes a drop (the last named of them). */
  level: number;
  /** Uploads them and says so (a toast). None: files can't go here (`label` says why). */
  take?: (files: File[]) => Promise<void>;
}

/** Each file of a dropped folder: its path from the folder dropped ("Acme/2026/Contrato.pdf"). */
const paths = new WeakMap<File, string>();

/** A dropped (or picked) file's path from the folder it came in ("Acme/2026/Contrato.pdf"); none: it came alone. */
export const droppedPath = (f: File): string | undefined => paths.get(f) ?? (f.webkitRelativePath || undefined);

/** The entries of a dropped folder, all of them (a reader gives them a batch at a time). */
async function entriesOf(dir: FileSystemDirectoryEntry): Promise<FileSystemEntry[]> {
  const reader = dir.createReader();
  const out: FileSystemEntry[] = [];
  for (;;) {
    // biome-ignore lint/performance/noAwaitInLoops: a reader gives its batches one after another.
    const batch = await new Promise<FileSystemEntry[]>((ok, fail) => reader.readEntries(ok, fail));
    if (batch.length === 0) {
      return out;
    }
    out.push(...batch);
  }
}

/** The files under `entry`, each with its path from where the drop began. */
async function filesUnder(entry: FileSystemEntry): Promise<File[]> {
  if (entry.isFile) {
    const file = await new Promise<File>((ok, fail) => (entry as FileSystemFileEntry).file(ok, fail));
    paths.set(file, entry.fullPath.replace(LEADING_SLASH, ""));
    return [file];
  }
  const inside = await entriesOf(entry as FileSystemDirectoryEntry);
  return (await Promise.all(inside.map(filesUnder))).flat();
}

const LEADING_SLASH = /^\/+/u;

/**
 * What was dropped, as files: a dropped folder read through (each file with its path in it, `droppedPath`), loose files
 * as they are. Called during the drop (the browser keeps what was dropped only then).
 */
export function droppedFiles(dt: DataTransfer | null | undefined): Promise<File[]> {
  const entries = [...(dt?.items ?? [])].map((i) => i.webkitGetAsEntry?.() ?? null);
  if (!entries.some((e) => e?.isDirectory)) {
    return Promise.resolve([...(dt?.files ?? [])]);
  }
  return Promise.all(entries.map((e) => (e ? filesUnder(e) : Promise.resolve([])))).then((all) => all.flat());
}

/** The targets named by what's shown, each as its latest render has it. */
const named = signal<readonly { current: DropTarget | null }[]>([]);
/** Uploads under way, by target key. */
const running = signal<ReadonlyMap<string, number>>(new Map());
/** The target of each upload begun by a drop on the window, while it runs (for the overlay's progress). */
export const dropping = signal<readonly DropTarget[]>([]);

/** Names `target` while the component is shown (none: nothing to name now). */
export function useDropTarget(target: DropTarget | null): void {
  const ref = useRef(target);
  ref.current = target;
  const on = target !== null;
  useEffect(() => {
    if (!on) {
      return;
    }
    named.value = [...named.value, ref];
    return () => {
      named.value = named.value.filter((x) => x !== ref);
    };
  }, [on]);
}

/** Where a drop goes now: of the targets named, the highest level, the last named of it (none: no page takes files). */
export function dropTarget(): DropTarget | null {
  let best: DropTarget | null = null;
  for (const { current } of named.value) {
    if (current && (!best || current.level >= best.level)) {
      best = current;
    }
  }
  return best;
}

function count(key: string, by: number): void {
  const next = new Map(running.value);
  next.set(key, (next.get(key) ?? 0) + by);
  running.value = next;
}

/** Whether files are uploading into `key` (from a drop zone, or dropped on the window). */
export const uploading = (key: string): boolean => (running.value.get(key) ?? 0) > 0;

/** Uploads `files` into `target`, a failure said in a toast. */
export async function upload(target: DropTarget, files: File[]): Promise<void> {
  if (!target.take || files.length === 0) {
    return;
  }
  count(target.key, 1);
  try {
    await target.take(files);
  } catch (e) {
    toast(asApiError(e).message, "error");
  } finally {
    count(target.key, -1);
  }
}

/** [`upload`], begun by a drop on the window: the overlay shows it under way. */
export async function uploadDropped(target: DropTarget, files: File[]): Promise<void> {
  dropping.value = [...dropping.value, target];
  try {
    await upload(target, files);
  } finally {
    const i = dropping.value.indexOf(target);
    dropping.value = dropping.value.filter((_, j) => j !== i);
  }
}
